import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
  Fade,
  Zoom,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  RoomService,
  Visibility,
  Category,
  AccessTime,
  AttachMoney,
  CheckCircle,
  Refresh,
  Download,
} from '@mui/icons-material';
import DataTable, { Column } from '../components/common/DataTable';
import ServiceForm from '../components/services/ServiceForm';
import DeleteServiceDialog from '../components/services/DeleteServiceDialog';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import { serviceService, CreateServiceData } from '../services/serviceService';

interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  duration: number;
  price: number;
  discountPrice?: number;
  isActive: boolean;
  requiredSkills: string[];
  images: string[];
  createdAt: string;
}

const columns: Column[] = [
  {
    id: 'name',
    label: 'Service',
    format: (value: string, row: Service) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: '#E8B4B8',
            color: 'white',
            width: 40,
            height: 40,
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          <RoomService sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.category} - {row.subcategory}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    id: 'duration',
    label: 'Duration',
    format: (value: number) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTime sx={{ fontSize: 16, color: '#E8B4B8' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value} min
        </Typography>
      </Box>
    ),
  },
  {
    id: 'price',
    label: 'Price',
    format: (value: number, row: Service) => (
      <Box>
        {row.discountPrice ? (
          <>
            <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.8rem' }}>
              ₹{value.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: '#48BB78', fontWeight: 700 }}>
              ₹{row.discountPrice.toLocaleString()}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            ₹{value.toLocaleString()}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'isActive',
    label: 'Status',
    format: (value: boolean) => (
      <Chip
        label={value ? 'Active' : 'Inactive'}
        sx={{
          bgcolor: value ? 'rgba(72, 187, 120, 0.1)' : 'rgba(160, 174, 192, 0.1)',
          color: value ? '#48BB78' : '#A0AEC0',
          fontWeight: 600,
          border: `1px solid ${value ? '#48BB78' : '#A0AEC0'}30`,
        }}
        size="small"
      />
    ),
  },
];

const Services: React.FC = () => {
  const dispatch = useAppDispatch();
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [error, setError] = useState<string | null>(null);

  // Load services from API
  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await serviceService.getServices({
        search: searchTerm || undefined,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      });
      setServices(response.services);
      setFilteredServices(response.services);
      setTotalCount(response.total || 0);
    } catch (err) {
      console.error('Failed to load services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load services');
      setServices([]);
      setFilteredServices([]);
      setTotalCount(0);
      dispatch(addNotification({ type: 'error', message: 'Failed to load services' }));
    } finally {
      setLoading(false);
    }
  };

  // Load services on component mount and when filters change
  useEffect(() => {
    loadServices();
  }, [page, rowsPerPage, searchTerm, categoryFilter]);

  const handleView = (service: Service) => {
    setSelectedService(service);
    setViewDialogOpen(true);
  };

  const handleEdit = (service: Service) => {
    setSelectedService(service);
    setFormMode('edit');
    setFormDialogOpen(true);
  };

  const handleDelete = (service: Service) => {
    setSelectedService(service);
    setDeleteDialogOpen(true);
  };

  const handleCreate = () => {
    setSelectedService(null);
    setFormMode('create');
    setFormDialogOpen(true);
  };

  const handleFormSubmit = async (data: CreateServiceData) => {
    try {
      setLoading(true);
      if (formMode === 'create') {
        await serviceService.createService(data);
        dispatch(addNotification({
          type: 'success',
          message: 'Service created successfully',
        }));
      } else if (selectedService) {
        await serviceService.updateService({ ...data, id: selectedService.id });
        dispatch(addNotification({
          type: 'success',
          message: 'Service updated successfully',
        }));
      }
      loadServices();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: `Failed to ${formMode} service: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedService) return;

    try {
      setLoading(true);
      await serviceService.deleteService(selectedService.id);
      dispatch(addNotification({
        type: 'success',
        message: 'Service deleted successfully',
      }));
      setDeleteDialogOpen(false);
      loadServices();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: `Failed to delete service: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
    } finally {
      setLoading(false);
    }
  };

  const getServiceStats = () => {
    return {
      total: services.length,
      active: services.filter(s => s.isActive).length,
      categories: [...new Set(services.map(s => s.category))].length,
      avgPrice: services.length > 0 ? Math.round(services.reduce((sum, s) => sum + s.price, 0) / services.length) : 0,
    };
  };

  const stats = getServiceStats();

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 3,
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Service Management
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Manage your salon services and pricing efficiently
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh Services" arrow>
                <IconButton
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onClick={loadServices}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Services" arrow>
                <IconButton
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreate}
                disabled={loading}
                sx={{
                  bgcolor: 'white',
                  color: '#2D3748',
                  fontWeight: 800,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    color: '#1A202C',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
                    border: '2px solid white',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    color: '#718096',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .MuiButton-startIcon': {
                    color: '#2D3748',
                  },
                }}
              >
                Add Service
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Fade in>
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 3,
              border: '1px solid rgba(237, 137, 54, 0.3)',
              bgcolor: 'rgba(237, 137, 54, 0.1)',
            }}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Enhanced Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Total Services
                    </Typography>
                    <Typography
                      variant="h3"
                      component="div"
                      sx={{
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {loading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : stats.total}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <RoomService sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        All offerings
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      width: 70,
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <RoomService sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(240, 147, 251, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(240, 147, 251, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Active Services
                    </Typography>
                    <Typography
                      variant="h3"
                      component="div"
                      sx={{
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {loading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : stats.active}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Available now
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      width: 70,
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(79, 172, 254, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(79, 172, 254, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Categories
                    </Typography>
                    <Typography
                      variant="h3"
                      component="div"
                      sx={{
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {stats.categories}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Category sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Service types
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      width: 70,
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Category sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(250, 112, 154, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(250, 112, 154, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Average Price
                    </Typography>
                    <Typography
                      variant="h3"
                      component="div"
                      sx={{
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2rem',
                      }}
                    >
                      ₹{stats.avgPrice}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <AttachMoney sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Per service
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      width: 70,
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <AttachMoney sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Filters Section */}
      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Card
          sx={{
            mb: 3,
            mt: 2,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #E8B4B8 0%, #D4969B 100%)',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)',
                }}
              >
                <Search sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                  Search & Filter
                </Typography>
                <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 500 }}>
                  Find and organize your services efficiently
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: 2, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 3, border: '1px solid rgba(232, 180, 184, 0.1)' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search services..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '& fieldset': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#E8B4B8', fontSize: 24 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#E8B4B8' }}>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      label="Category"
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      sx={{
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                      }}
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      <MenuItem value="Makeup">Makeup</MenuItem>
                      <MenuItem value="Hair">Hair</MenuItem>
                      <MenuItem value="Skincare">Skincare</MenuItem>
                      <MenuItem value="Nails">Nails</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Chip
                      label={`${(error ? filteredServices : services).length} Services Found`}
                      sx={{
                        background: 'linear-gradient(135deg, #E8B4B8 0%, #D4969B 100%)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        height: 36,
                        boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(232, 180, 184, 0.4)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Services Table */}
      <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
        <Card
          sx={{
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                }}
              >
                <RoomService sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                  Services Directory
                </Typography>
                <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 500 }}>
                  Complete list of your salon services
                </Typography>
              </Box>
            </Box>

            <DataTable
              columns={columns}
              rows={error ? filteredServices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : filteredServices}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount || 0}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading}
            />
          </Box>
        </Card>
      </Fade>

      {/* Service Form Dialog */}
      <ServiceForm
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        mode={formMode}
        initialData={selectedService ? {
          name: selectedService.name,
          description: selectedService.description,
          category: selectedService.category,
          subcategory: selectedService.subcategory,
          duration: selectedService.duration,
          price: selectedService.price,
          discountPrice: selectedService.discountPrice,
          isActive: selectedService.isActive,
          requiredSkills: selectedService.requiredSkills,
        } : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteServiceDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        service={selectedService}
        loading={loading}
      />

      {/* Service Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Service Details</Typography>
            <Chip
              label={selectedService?.isActive ? 'Active' : 'Inactive'}
              color={selectedService?.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedService && (
            <Box>
              <Typography variant="h5" gutterBottom>
                {selectedService.name}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {selectedService.description}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {selectedService.category} - {selectedService.subcategory}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1">
                    {selectedService.duration} minutes
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Regular Price
                  </Typography>
                  <Typography variant="h6">
                    ₹{selectedService.price}
                  </Typography>
                </Grid>
                {selectedService.discountPrice && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Discount Price
                    </Typography>
                    <Typography variant="h6" color="primary">
                      ₹{selectedService.discountPrice}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {selectedService.requiredSkills.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Required Skills
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedService.requiredSkills.map((skill) => (
                      <Chip key={skill} label={skill} variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => handleEdit(selectedService!)}>
            Edit Service
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;